import qs from 'qs';
import type {
    SellerOverviewWithFavouriteSellerStatusDto,
    SellersOfInterestApiResponse,
    StartPageSellersOfInterestRequest,
    WebGetSellerByAliasSearchApiResponse,
} from 'src/state/services/types/webapi-discover-generated';
import { isServer } from 'tradera/utils/nextjs';
import type { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const PAGE_SIZE = 10;

export type ParsedSellers = {
    sellers?: SellerOverviewWithFavouriteSellerStatusDto[] | null;
    totalSellers: number;
    totalPages: number;
};

type FollowParams = {
    sellerId: number;
    search: { query: string; page: string };
};

function mutateSeller(
    { sellers }: MaybeDrafted<ParsedSellers>,
    sellerId: number,
    follow: boolean,
) {
    const member = sellers?.find((m) => m.memberId === sellerId);
    if (member) {
        // eslint-disable-next-line better-mutation/no-mutation
        member.existsInFavouriteSellers = follow;
    }
}

export const memberSearchApi = createApi({
    reducerPath: 'memberSearchApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '',
    }),
    endpoints: (builder) => ({
        getSearchSellers: builder.query<
            ParsedSellers,
            {
                query: string;
                page: string;
            }
        >({
            query: ({ query, page }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/seller-list/search?query=${query}&page=${page}&pageSize=${PAGE_SIZE}`;
            },
            transformResponse: (
                response: WebGetSellerByAliasSearchApiResponse,
            ) => {
                const totalSellers = response.totalSellers;
                return {
                    sellers: response.sellerOverviews,
                    totalSellers,
                    totalPages: Math.ceil(totalSellers / PAGE_SIZE),
                };
            },
        }),
        getSellersOfInterest: builder.query<
            ParsedSellers,
            StartPageSellersOfInterestRequest
        >({
            query: ({ shippingCountryCode }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    maxSellers: PAGE_SIZE,
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/sellers-of-interest?${query}`;
            },
            transformResponse: (response: SellersOfInterestApiResponse) => {
                const totalSellers = response.sellerCards?.length || 0;
                return {
                    sellers: response.sellerCards?.map((seller) => ({
                        memberId: seller.sellerId,
                        alias: seller.sellerAlias,
                        existsInFavouriteSellers: !seller.isSuggestion,
                        heroImageUrl: seller.heroImageUrl || '',
                        numberOfActiveItems: seller.matchedItems,
                    })),
                    totalSellers,
                    totalPages: Math.ceil(totalSellers / PAGE_SIZE),
                };
            },
        }),
        followSeller: builder.mutation<void, FollowParams>({
            query: (params) => ({
                url: '/my/favourite-sellers/add',
                method: 'POST',
                body: { sellerId: params.sellerId },
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    params.search.query
                        ? memberSearchApi.util.updateQueryData(
                              'getSearchSellers',
                              {
                                  ...params.search,
                              },
                              (draft) =>
                                  mutateSeller(draft, params.sellerId, true),
                          )
                        : memberSearchApi.util.updateQueryData(
                              'getSellersOfInterest',
                              {},
                              (draft) =>
                                  mutateSeller(draft, params.sellerId, true),
                          ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        unfollowSeller: builder.mutation<void, FollowParams>({
            query: (params) => ({
                url: '/my/favourite-sellers/remove',
                method: 'POST',
                body: { ids: [params.sellerId] },
            }),
            async onQueryStarted(params, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    params.search.query
                        ? memberSearchApi.util.updateQueryData(
                              'getSearchSellers',
                              {
                                  ...params.search,
                              },
                              (draft) =>
                                  mutateSeller(draft, params.sellerId, false),
                          )
                        : memberSearchApi.util.updateQueryData(
                              'getSellersOfInterest',
                              {},
                              (draft) =>
                                  mutateSeller(draft, params.sellerId, false),
                          ),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
});

export const {
    useGetSearchSellersQuery,
    useGetSellersOfInterestQuery,
    useFollowSellerMutation,
    useUnfollowSellerMutation,
} = memberSearchApi;
